import InvoiceCard from "./InvoiceCard";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { useInvoicesInfiniteQuery } from "@features/invoices/hooks/useInvoices";
import InfiniteScrollTrigger from "@shared/components/InfiniteScrollTrigger";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Input } from "@shared/components/ui/input";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import {
  Search,
  Filter,
  SortAsc,
  SortDesc,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  DollarSign,
} from "lucide-react";

const InvoicesList = () => {
  const { t } = useTranslation();
  const [filters] = useState({ limit: 10 });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const {
    data,
    error,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInvoicesInfiniteQuery(filters);

  // Get all invoices from all pages - wrapped in useMemo to avoid recreation
  const allInvoices = useMemo(() => {
    return data?.pages?.flatMap((page) => page.data) || [];
  }, [data]);

  // Filter and sort invoices
  const filteredAndSortedInvoices = useMemo(() => {
    if (!allInvoices.length) return [];

    let filtered = allInvoices.filter((invoice) => {
      const matchesSearch =
        invoice.client?.brandName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        invoice.client?.phone?.includes(searchTerm) ||
        invoice._id.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || invoice.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "createdAt":
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case "totalNet":
          aValue = a.totalNet || 0;
          bValue = b.totalNet || 0;
          break;
        case "packageCount":
          aValue = a.packages?.length || 0;
          bValue = b.packages?.length || 0;
          break;
        case "client":
          aValue = a.client?.brandName?.toLowerCase() || "";
          bValue = b.client?.brandName?.toLowerCase() || "";
          break;
        default:
          return 0;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [allInvoices, searchTerm, statusFilter, sortBy, sortOrder]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!allInvoices.length)
      return {
        total: 0,
        pending: 0,
        paid: 0,
        overdue: 0,
        totalRevenue: 0,
        totalPackages: 0,
      };

    const stats = {
      total: allInvoices.length,
      pending: allInvoices.filter((i) => i.status === "pending").length,
      paid: allInvoices.filter((i) => i.status === "paid").length,
      overdue: allInvoices.filter((i) => i.status === "overdue").length,
      totalRevenue: allInvoices.reduce((sum, i) => sum + (i.totalNet || 0), 0),
      totalPackages: allInvoices.reduce(
        (sum, i) => sum + (i.packages?.length || 0),
        0
      ),
    };

    return stats;
  }, [allInvoices]);

  if (error) {
    toast.error("Error fetching invoices", error.message);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <ThreeDotLoader />
      </div>
    );
  }

  if (!allInvoices.length && !searchTerm && statusFilter === "all") {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <FileText className="size-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t("invoices.noInvoices")}
          </h3>
          <p className="text-gray-600 mb-4">
            {t("invoices.noInvoicesDescription")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4 text-center">
            <FileText className="size-6 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold text-blue-700">
              {stats.total}
            </div>
            <div className="text-sm text-gray-600">
              {t("invoices.totalInvoices")}
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-4 text-center">
            <Clock className="size-6 mx-auto mb-2 text-yellow-600" />
            <div className="text-2xl font-bold text-yellow-700">
              {stats.pending}
            </div>
            <div className="text-sm text-gray-600">{t("status.pending")}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4 text-center">
            <CheckCircle2 className="size-6 mx-auto mb-2 text-green-600" />
            <div className="text-2xl font-bold text-green-700">
              {stats.paid}
            </div>
            <div className="text-sm text-gray-600">{t("status.paid")}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4 text-center">
            <XCircle className="size-6 mx-auto mb-2 text-red-600" />
            <div className="text-2xl font-bold text-red-700">
              {stats.overdue}
            </div>
            <div className="text-sm text-gray-600">{t("status.overdue")}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4 text-center">
            <DollarSign className="size-6 mx-auto mb-2 text-purple-600" />
            <div className="text-2xl font-bold text-purple-700">
              {stats.totalRevenue.toFixed(2)}
            </div>
            <div className="text-sm text-gray-600">
              {t("invoices.totalRevenue")} ({t("common.currency")})
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="size-5" />
            {t("common.filtersAndSearch")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 size-4 text-gray-400" />
              <Input
                placeholder={t("invoices.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder={t("common.status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.allStatuses")}</SelectItem>
                <SelectItem value="pending">{t("status.pending")}</SelectItem>
                <SelectItem value="paid">{t("status.paid")}</SelectItem>
                <SelectItem value="overdue">{t("status.overdue")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort By */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder={t("common.sortBy")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt">
                  {t("common.dateCreated")}
                </SelectItem>
                <SelectItem value="totalNet">{t("common.amount")}</SelectItem>
                <SelectItem value="packageCount">
                  {t("packages.count")}
                </SelectItem>
                <SelectItem value="client">{t("client.name")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort Order */}
            <Button
              variant="outline"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              className="flex items-center gap-2"
            >
              {sortOrder === "asc" ? (
                <SortAsc className="size-4" />
              ) : (
                <SortDesc className="size-4" />
              )}
              {sortOrder === "asc"
                ? t("common.ascending")
                : t("common.descending")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">
          {t("invoices.invoices")} ({filteredAndSortedInvoices.length})
        </h3>

        {filteredAndSortedInvoices.length > 0 && (
          <Badge variant="outline" className="text-sm">
            {filteredAndSortedInvoices.length} {t("common.results")}
          </Badge>
        )}
      </div>

      {/* Invoice Cards */}
      {filteredAndSortedInvoices.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {filteredAndSortedInvoices.map((invoice) => (
            <InvoiceCard key={invoice._id} invoice={invoice} />
          ))}
        </div>
      ) : (
        <Card className="text-center py-8">
          <CardContent>
            <Search className="size-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("common.noResults")}
            </h3>
            <p className="text-gray-600">{t("common.tryDifferentFilters")}</p>
          </CardContent>
        </Card>
      )}

      {/* Infinite Scroll Trigger */}
      <InfiniteScrollTrigger
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
      />
    </div>
  );
};

export default InvoicesList;
